import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Trophy, Medal, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function Leaderboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null); // "csv" | "pdf" | null

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leaderboard");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await fetch(`${API}/leaderboard/export?format=${format}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export échoué");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `classement-buteurs-${today}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        format === "pdf" ? "PDF téléchargé" : "CSV téléchargé",
      );
    } catch (e) {
      toast.error("Impossible d'exporter le classement");
    } finally {
      setExporting(null);
    }
  };

  const top3 = data.slice(0, 3);
  const rest = data.slice(3);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
            Classement
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
            Buteurs
          </h1>
        </div>
        {data.length > 0 && (
          <div className="no-print flex flex-wrap gap-2">
            <button
              data-testid="export-leaderboard-pdf"
              onClick={() => handleExport("pdf")}
              disabled={exporting !== null}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold text-xs uppercase tracking-wide disabled:opacity-50 transition-colors"
            >
              <FileText className="w-4 h-4" />
              {exporting === "pdf" ? "Export..." : "PDF"}
            </button>
            <button
              data-testid="export-leaderboard-csv"
              onClick={() => handleExport("csv")}
              disabled={exporting !== null}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs uppercase tracking-wide border border-white/10 disabled:opacity-50 transition-colors"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {exporting === "csv" ? "Export..." : "CSV"}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>
      ) : data.length === 0 ? (
        <div className="p-8 rounded-2xl border border-dashed border-white/10 text-center text-slate-500">
          Aucune statistique enregistrée pour l'instant
        </div>
      ) : (
        <>
          {/* Podium */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {top3.map((p, i) => {
              const rankColor = ["#E5B85C", "#94A3B8", "#DC2626"][i];
              const rankLabel = ["1er", "2ème", "3ème"][i];
              return (
                <div
                  key={p.player_id}
                  data-testid={`top-${i + 1}`}
                  className="rounded-2xl bg-slate-900 border border-white/10 p-6 relative overflow-hidden"
                >
                  <div
                    className="absolute top-0 left-0 h-1.5 w-full"
                    style={{ background: rankColor }}
                  />
                  <div className="flex items-center gap-3 mb-4">
                    {i === 0 ? (
                      <Trophy className="w-8 h-8" style={{ color: rankColor }} />
                    ) : (
                      <Medal className="w-8 h-8" style={{ color: rankColor }} />
                    )}
                    <div className="font-display font-black text-2xl uppercase" style={{ color: rankColor }}>
                      {rankLabel}
                    </div>
                  </div>
                  <div className="font-display font-bold text-xl uppercase truncate">
                    {p.player_name}
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                    <div className="p-2 rounded bg-slate-800">
                      <div className="text-[10px] font-mono-stat uppercase text-slate-400">Buts marqués</div>
                      <div className="font-mono-stat font-bold text-2xl" style={{ color: rankColor }}>
                        {p.goals}
                      </div>
                    </div>
                    <div className="p-2 rounded bg-slate-800">
                      <div className="text-[10px] font-mono-stat uppercase text-slate-400">Assists</div>
                      <div className="font-mono-stat font-bold text-2xl">{p.assists}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Table */}
          <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
            <div className="p-5 border-b border-white/10">
              <div className="font-display font-bold text-lg uppercase">Classement complet</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 border-b border-white/5">
                    <th className="p-3 text-left">#</th>
                    <th className="p-3 text-left">Joueur</th>
                    <th className="p-3 text-right">Buts marqués</th>
                    <th className="p-3 text-right">Assists</th>
                    <th className="p-3 text-right">Matchs joués</th>
                    <th className="p-3 text-right">Cartons jaunes</th>
                    <th className="p-3 text-right">Cartons rouges</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((p, i) => (
                    <tr
                      key={p.player_id}
                      data-testid={`leaderboard-row-${p.player_id}`}
                      className="border-b border-white/5 hover:bg-slate-800/50"
                    >
                      <td className="p-3 font-mono-stat font-bold text-slate-500">{i + 1}</td>
                      <td className="p-3 font-bold">{p.player_name}</td>
                      <td className="p-3 text-right font-mono-stat font-bold text-[#E5B85C]">
                        {p.goals}
                      </td>
                      <td className="p-3 text-right font-mono-stat text-cyan-300">{p.assists}</td>
                      <td className="p-3 text-right font-mono-stat text-slate-400">
                        {p.matches_played}
                      </td>
                      <td className="p-3 text-right font-mono-stat text-amber-300">
                        {p.yellow_cards}
                      </td>
                      <td className="p-3 text-right font-mono-stat text-rose-300">
                        {p.red_cards}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
